import { ApiError, type ApiResponse } from '@/types'
import { nowISO } from '@/lib/utils'

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export interface RequestContext {
  method: HttpMethod
  /** Path with the API base already stripped, e.g. `/plans/abc/items`. */
  path: string
  params: Record<string, string>
  query: URLSearchParams
  body: unknown
  headers: Record<string, string>
}

export type RouteHandler = (ctx: RequestContext) => Promise<unknown>

export interface Route {
  method: HttpMethod
  /** Express-style pattern, e.g. `/plans/:planId/items`. */
  pattern: string
  handler: RouteHandler
  /** Shown in Admin -> Public API as living documentation. */
  description: string
  /** Public routes are reachable without an authenticated session. */
  visibility: 'admin' | 'public'
}

interface CompiledRoute extends Route {
  regex: RegExp
  paramNames: string[]
}

function compile(route: Route): CompiledRoute {
  const paramNames: string[] = []
  const source = route.pattern
    .split('/')
    .map((segment) => {
      if (!segment.startsWith(':')) return segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      paramNames.push(segment.slice(1))
      return '([^/]+)'
    })
    .join('/')
  return { ...route, paramNames, regex: new RegExp(`^${source}/?$`) }
}

/**
 * A tiny Express-shaped router.
 *
 * Every route resolves against IndexedDB, so the whole "server" runs in the
 * browser tab — but the call sites are written exactly as they would be
 * against a real Express app, which is what makes the backend swappable.
 */
export class MockRouter {
  private routes: CompiledRoute[] = []

  register(routes: Route[]): this {
    this.routes.push(...routes.map(compile))
    return this
  }

  /** Route table, for the self-documenting Admin -> Public API tab. */
  list(visibility?: Route['visibility']): Route[] {
    return this.routes
      .filter((r) => !visibility || r.visibility === visibility)
      .map(({ method, pattern, description, visibility: v }) => ({
        method,
        pattern,
        description,
        visibility: v,
        handler: async () => undefined,
      }))
  }

  async dispatch<T>(
    method: HttpMethod,
    url: string,
    body?: unknown,
    headers: Record<string, string> = {},
  ): Promise<ApiResponse<T>> {
    const [rawPath, rawQuery = ''] = url.split('?')
    const path = rawPath.replace(/\/+$/, '') || '/'
    const query = new URLSearchParams(rawQuery)

    const pathMatches = this.routes.filter((route) => route.regex.test(path))
    if (pathMatches.length === 0) {
      throw new ApiError(404, 'not_found', `No route matches ${method} ${path}`)
    }

    const route = pathMatches.find((r) => r.method === method)
    if (!route) {
      throw new ApiError(
        405,
        'method_not_allowed',
        `${method} is not supported for ${path}. Allowed: ${pathMatches
          .map((r) => r.method)
          .join(', ')}`,
      )
    }

    const match = route.regex.exec(path)
    const params: Record<string, string> = {}
    route.paramNames.forEach((name, index) => {
      params[name] = decodeURIComponent(match?.[index + 1] ?? '')
    })

    const data = (await route.handler({ method, path, params, query, body, headers })) as T

    return {
      ok: true,
      status: method === 'POST' ? 201 : 200,
      meta: {
        endpoint: path,
        version: 'v1',
        generatedAt: nowISO(),
        count: Array.isArray(data) ? data.length : undefined,
      },
      data,
    }
  }
}

/** Throws a 404 when a lookup came back empty. */
export function required<T>(value: T | undefined | null, entity: string, id?: string): T {
  if (value === undefined || value === null) {
    throw new ApiError(404, 'not_found', `${entity}${id ? ` "${id}"` : ''} was not found`)
  }
  return value
}

/** Narrows an untyped request body, throwing a 400 when it is not an object. */
export function requireBody<T extends object>(body: unknown, what = 'payload'): T {
  if (!body || typeof body !== 'object' || Array.isArray(body)) {
    throw new ApiError(400, 'invalid_body', `A JSON object ${what} is required`)
  }
  return body as T
}
